import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchResidence, createEnquiry } from "@/lib/api";
import { useAuth } from "@/contexts/AuthProvider";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandInput, { BrandField, BrandTextarea } from "@/components/brand/BrandInput";
import DateRangePicker from "@/components/DateRangePicker";
import { Check } from "lucide-react";
import { toast } from "sonner";

const BrionjEnquiry = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [residence, setResidence] = useState(null);
  const [form, setForm] = useState({ check_in: "", check_out: "", guests: 2, bedrooms: 2, name: user?.name || "", email: user?.email || "", phone: "", notes: "", budget: "" });
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchResidence(id).then(setResidence); }, [id]);
  useEffect(() => { if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email })); }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const res = await createEnquiry({
        residence_id: residence.id,
        check_in: form.check_in, check_out: form.check_out,
        guests: form.guests, bedrooms: form.bedrooms,
        guest_name: form.name, guest_email: form.email, guest_phone: form.phone,
        notes: form.notes, budget: form.budget,
      });
      setSubmitted(res);
      toast.success("Enquiry received — we will be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enquiry failed");
    } finally { setBusy(false); }
  };

  if (!residence) return <div className="container-rail py-16">Loading…</div>;

  if (submitted) {
    return (
      <div className="container-rail py-20 md:py-28" data-testid="brionj-enquiry-confirmation">
        <div className="max-w-[680px]">
          <div className="inline-flex items-center gap-3 px-3 py-2 border border-[hsl(var(--brand-accent))] text-[hsl(var(--brand-accent))] text-[11px] uppercase tracking-[0.4em]"><Check size={12} /> Enquiry received</div>
          <BrandHeading level={1} className="mt-8">Thank you, {submitted.guest_name.split(" ")[0]}.</BrandHeading>
          <p className="mt-6 text-[16px] md:text-[17px] font-body leading-[1.85] text-foreground/80 editorial-measure">Your interest in <span className="italic text-[hsl(var(--brand-accent))]">{submitted.residence_name}</span> has been received. A house representative will be in touch within 24 hours to discuss arrangements.</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-6 text-[14px]">
            <div><BrandLabel>Reference</BrandLabel><div className="mt-2 font-mono text-[13px]">{submitted.id}</div></div>
            <div><BrandLabel>Status</BrandLabel><div className="mt-2">New</div></div>
            <div><BrandLabel>Dates</BrandLabel><div className="mt-2">{submitted.check_in || "—"} {submitted.check_out && `→ ${submitted.check_out}`}</div></div>
            <div><BrandLabel>Guests</BrandLabel><div className="mt-2">{submitted.guests} guests · {submitted.bedrooms} bedrooms</div></div>
          </div>
          <div className="mt-12 flex gap-3 flex-wrap"><BrandButton to="/bookings" data-testid="view-enquiries">View your enquiries</BrandButton><BrandButton to="/brionj/collection" variant="ghost" data-testid="back-collection">Back to the collection</BrandButton></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-rail py-12 md:py-20 grid md:grid-cols-[1.4fr_1fr] gap-12 items-start" data-testid="brionj-enquiry-form">
      <div>
        <BrandLabel withRule>Begin an enquiry</BrandLabel>
        <BrandHeading level={1} className="mt-6"><span className="italic">{residence.name}</span></BrandHeading>
        <p className="mt-4 text-[12px] uppercase tracking-[0.4em] text-[hsl(var(--brand-accent))]">{residence.location} · {residence.bedrooms} bedrooms</p>
        <form onSubmit={submit} className="mt-10 grid gap-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <BrandField label="Arrive — Depart (optional)" className="sm:col-span-2">
              <DateRangePicker
                mode="range"
                value={{ from: form.check_in, to: form.check_out }}
                onChange={(v) => setForm((f) => ({ ...f, check_in: v.from || "", check_out: v.to || "" }))}
                placeholder="Choose your dates"
                testId="enq-date-range"
              />
            </BrandField>
            <BrandField label="Guests" required><BrandInput data-testid="enq-guests" type="number" min={1} max={30} value={form.guests} onChange={(e) => set("guests", Number(e.target.value || 1))} /></BrandField>
            <BrandField label="Bedrooms" required><BrandInput data-testid="enq-bedrooms" type="number" min={1} max={20} value={form.bedrooms} onChange={(e) => set("bedrooms", Number(e.target.value || 1))} /></BrandField>
          </div>
          <div className="h-px bg-[hsl(var(--brand-accent))]/30 my-2" />
          <div className="grid sm:grid-cols-2 gap-5">
            <BrandField label="Your name" required><BrandInput data-testid="enq-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></BrandField>
            <BrandField label="Email" required><BrandInput data-testid="enq-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></BrandField>
            <BrandField label="Phone"><BrandInput data-testid="enq-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></BrandField>
            <BrandField label="Indicative budget"><BrandInput data-testid="enq-budget" placeholder="e.g. €40,000+" value={form.budget} onChange={(e) => set("budget", e.target.value)} /></BrandField>
          </div>
          <BrandField label="Notes"><BrandTextarea data-testid="enq-notes" rows={5} placeholder="What kind of stay are you imagining? Anything we should know about your party or arrival?" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></BrandField>
          <div className="mt-2 flex items-center justify-between"><Link to="/brionj/collection" className="text-[11px] uppercase tracking-[0.4em] text-foreground/60 hover:text-foreground">Cancel</Link><BrandButton type="submit" disabled={busy} data-testid="submit-enquiry">{busy ? "Sending…" : "Submit Enquiry"}</BrandButton></div>
        </form>
      </div>
      <aside className="md:sticky md:top-24 self-start">
        <div className="p-6 bg-[hsl(var(--card))] border border-border">
          <BrandLabel>The residence</BrandLabel>
          <img src={residence.hero_image} alt={residence.name} className="mt-4 w-full aspect-[4/3] object-cover" />
          <div className="mt-5 font-display italic text-[26px] text-[hsl(var(--brand-accent))]">{residence.name}</div>
          <div className="mt-2 text-[11px] uppercase tracking-[0.32em] text-foreground/60">{residence.country}</div>
          <p className="mt-4 text-[14px] font-body text-foreground/80 leading-relaxed">{residence.tagline}</p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-[12px] uppercase tracking-[0.22em] text-foreground/60">
            <div>{residence.bedrooms} bedrooms</div>
            <div>{residence.bathrooms} bath</div>
            <div>Sleeps {residence.sleeps}</div>
            <div>{residence.indicative_price || "By enquiry"}</div>
          </div>
        </div>
      </aside>
    </div>
  );
};
export default BrionjEnquiry;
